import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { formatTrackDuration } from "../../../helpers/dateTime";
import { getCountryNameByCode } from "../../../helpers/countryHelper";
import { getLanguageNameByCode } from "../../../helpers/languageHelper";
import Card from "../../../common/components/Card/Card";
import { IListing, IMusic, INft } from "../../../common/commonTypes";
import NftIncludedItems from "../../../common/components/NftIncludedItems/NftIncludedItems";
import InfoIcon from "../../../common/components/InfoIcon/InfoIcon";
import Typography from "../../../common/components/Typography/Typography";
import { getBlockExplorerAddressUrl } from "../../../helpers/transactions";
import StaticLink from "../../../common/components/StaticLink/StaticLink";

const Root = styled(Card)`
  padding: 48px;
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -12px;
`;

const GridItem = styled.div`
  padding: 12px;
  flex-grow: 1;
  flex-basis: 50%;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-basis: 100%;
  }
`;

const Divider = styled.div`
  margin: 24px 0;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.darkBorderColor};
`;

const NftItemsTitle = styled(Typography)`
  margin-bottom: 24px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const MetadataItem = styled(GridItem)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface IProps {
  music: IMusic;
  nft?: INft;
  listing?: IListing;
}

const MusicDetails = ({ music, nft, listing }: IProps) => {
  const { t } = useTranslation();
  const durationFormatted = formatTrackDuration(music.master_length);

  const countriesList = music.countries
    ?.map((c) => getCountryNameByCode(c))
    .filter((c) => !!c)
    .join(", ");

  const languagesList = music.languages
    ?.map((l) => getLanguageNameByCode(l))
    .filter((c) => !!c)
    .join(", ");

  const genresList = music.genres?.join("/");

  return (
    <Root>
      <Grid>
        {countriesList && (
          <GridItem>
            <InfoIcon icon="globe" text={countriesList} />
          </GridItem>
        )}
        {genresList && (
          <GridItem>
            <InfoIcon icon="layers" text={genresList} />
          </GridItem>
        )}
        {languagesList && (
          <GridItem>
            <InfoIcon icon="flag" text={languagesList} />
          </GridItem>
        )}
        {durationFormatted && (
          <GridItem>
            <InfoIcon icon="length" text={durationFormatted} />
          </GridItem>
        )}
      </Grid>
      <Divider />
      <NftItemsTitle size="sm" secondary weight="400">
        {t("nft-content")}
      </NftItemsTitle>
      <Grid>
        <NftIncludedItems
          music={music}
          withRights={listing?.with_rights}
          renderItem={(item) => (
            <GridItem key={item.id}>
              <InfoIcon icon={item.icon} text={item.text} />
            </GridItem>
          )}
        />
      </Grid>
      <>
        <Divider />
        <Grid>
          {nft && (
            <>
              <MetadataItem>
                <Typography secondary>{t("contract-address")}</Typography>
                <Typography>
                  <StaticLink
                    href={getBlockExplorerAddressUrl(
                      nft.metadata.contract,
                      nft.listing.deployment.chain
                    )}
                    target="_blank"
                  >
                    <Typography>{nft.metadata.contract}</Typography>
                  </StaticLink>
                </Typography>
              </MetadataItem>
              <MetadataItem>
                <Typography secondary>{t("token-id")}</Typography>
                <Typography>{nft.metadata.id}</Typography>
              </MetadataItem>
            </>
          )}
          {listing && (
            <MetadataItem>
              <Typography secondary>{t("blockchain")}</Typography>
              <Typography>
                {listing?.deployment?.chain?.name || "Ethereum"}
              </Typography>
            </MetadataItem>
          )}
        </Grid>
      </>
    </Root>
  );
};

export default MusicDetails;
